import { type FocusEvent, type MouseEvent as ReactMouseEvent, type RefObject, useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch } from 'react-redux';

import {
  isHighlightedSelector,
  itemIsPreSelectedSelector,
  itemIsSelectedSelector,
  resetSelectedItemsAction,
  showShiftSelectHighlightsSelector,
} from '@air/redux-selected-items';

import { useIsInSelectAllSubtree } from '~/components/v2/hooks/useIsInSelectAllSubtree';
import { useFocusable } from '~/hooks/useFocusable';
import { type UseMultiselectItemsType, useMultiselectItem } from '~/hooks/useMultiselectItem';
import { modifiersToCommitParams } from '~/hooks/useSelectionCommit';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import { useAirSelector, useAirStore } from '~/utils/ReduxUtils';
import { useSelectShiftSelectedHighlightedItems } from './useSelectShiftSelectedHighlightedItems';

// NotSelectableUpload is here to satisfy TypeScript - TableView uses the same layout for SelectedItem and Uploads, and this hook is used there
export type UseSelectableCardProps<T extends SelectableGalleryItem> = {
  item: T;
  isSelectable: boolean;
  onClick?: (item: T['item']) => void;
};

/**
 * Modifier keys read directly off the originating mouse event. Sourcing shift/cmd
 * from the event (rather than a keydown-tracked ref) makes click-based selection
 * synchronous with the click — it can't lose a race with a key listener still
 * processing the keydown. Every selection entry point supplies these from its own
 * mouse event.
 */
export type SelectionMouseModifiers = {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
};

export type UseSelectableCardReturnType = {
  handleItemClick: (e?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  isHovering: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  handleHover: () => void;
  handleUnhover: () => void;
  onBlur: (e: FocusEvent<HTMLDivElement, Element>) => void;
  onFocus: (e: FocusEvent<HTMLDivElement, Element>) => void;
  confirmItemSelection: (modifiers: SelectionMouseModifiers) => void;
  handleItemDoubleClick: () => void;
  itemRef: RefObject<HTMLDivElement>;
  preSelectItem: (modifiers: SelectionMouseModifiers) => void;
};

export function useSelectableCard<T extends SelectableGalleryItem, E extends HTMLDivElement>({
  item: { id, type, item: data },
  item,
  isSelectable,
  onClick,
}: UseSelectableCardProps<T>): UseSelectableCardReturnType {
  const dispatch = useDispatch();
  const store = useAirStore();
  const isInReduxSelected = useAirSelector((st) => itemIsSelectedSelector(st, id));
  const isInSelectAllSubtree = useIsInSelectAllSubtree();

  // Item appears selected if in Redux OR if selectAll+includeNestedItems is active within subtree (for non-boards)
  const isSelected = isInReduxSelected || (isInSelectAllSubtree && type !== 'board');
  const isHighlighted = useAirSelector((st) => isHighlightedSelector(st, item.id));
  const { selectShiftSelectedHighlightedItems } = useSelectShiftSelectedHighlightedItems();

  const { isHovering, handleHover, handleUnhover, handleSelect, handleUnselect, handlePreSelect } = useMultiselectItem(
    id,
    type,
    data,
  );

  const handleShiftEnterPress = useCallback(() => {
    selectShiftSelectedHighlightedItems(item, 'shift+enter');
  }, [item, selectShiftSelectedHighlightedItems]);

  const select = useCallback(
    (params: Parameters<UseMultiselectItemsType['handleSelect']>[0]) => {
      if (isSelectable) {
        handleSelect(params);
      }
    },
    [isSelectable, handleSelect],
  );

  const preSelect = useCallback(
    (params: Parameters<UseMultiselectItemsType['handleSelect']>[0]) => {
      if (isSelectable) {
        handlePreSelect(params);
      }
    },
    [isSelectable, handlePreSelect],
  );

  const handleCmdEnterPress = useCallback(
    (e?: KeyboardEvent) => {
      e?.preventDefault();
      select({ trigger: 'meta+enter' });
    },
    [select],
  );

  const handleEnterPress = useCallback(() => {
    const trigger = 'enter';

    if (isSelected) {
      handleUnselect({ trigger });
    } else {
      dispatch(resetSelectedItemsAction());
      select({ trigger });
    }
  }, [dispatch, handleUnselect, isSelected, select]);

  // this is here to check if current element is active (so if user does not click e.g. ellipsis menu on card)
  // useHotKeys returns ref and checks it itself, but that handles all key events for defined keys, so in this case buttons did not receive enter event
  // this has been fixed by adding { enabled: isHovering }, but then ellipsis menu inside a card did not received enter event
  const itemRef = useRef<E>(null);

  useHotkeys(
    'meta+enter, ctrl+enter, shift+enter, enter',
    (event) => {
      if (itemRef.current === document.activeElement?.parentElement) {
        // Prevent multiple calls when user holds key down
        if (!event.repeat && event.key === 'Enter') {
          if (event.ctrlKey || event.metaKey) {
            handleCmdEnterPress(event);
          } else if (event.shiftKey) {
            handleShiftEnterPress();
          } else {
            handleEnterPress();
          }
        }
      }
    },
    {
      enabled: isHovering,
      preventDefault: true,
    },
    [handleCmdEnterPress, handleShiftEnterPress, handleEnterPress],
  );

  const handleItemClick = useCallback(
    (e?: ReactMouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      const multiselectHighlightIsShowing = showShiftSelectHighlightsSelector(store.getState());
      if (!multiselectHighlightIsShowing) {
        data && onClick?.(data);
      } else {
        e && select({ trigger: 'click' });
      }
    },
    [data, store, onClick, select],
  );

  const confirmItemSelection = useCallback(
    ({ shiftKey, metaKey, ctrlKey }: SelectionMouseModifiers) => {
      // Read the pre-selected flag FRESH from the store, not the closed-over selector value.
      // The stable mouseup owner (useConfirmPreSelectedItemOnMouseUp) may commit this same
      // press first and clear preSelectedIds; reading fresh makes this per-card confirm a
      // no-op in that case instead of double-committing (which would toggle the item back off).
      if (itemIsPreSelectedSelector(store.getState(), id)) {
        select(modifiersToCommitParams({ shiftKey, metaKey, ctrlKey }));
      }
    },
    [id, select, store],
  );

  const preSelectItem = useCallback(
    ({ shiftKey, metaKey, ctrlKey }: SelectionMouseModifiers) => {
      const { trigger, reset } = modifiersToCommitParams({ shiftKey, metaKey, ctrlKey });
      // cmd/ctrl-clicking an already-selected item unselects it on press.
      if ((metaKey || ctrlKey) && isSelected) {
        return handleUnselect({ trigger });
      }
      preSelect({ trigger, reset });
    },
    [handleUnselect, isSelected, preSelect],
  );

  const handleItemDoubleClick = () => {
    handleUnselect({ trigger: 'double-click' });
    handleItemClick();
  };
  const [onFocus, onBlur] = useFocusable(handleHover, handleUnhover);

  return {
    handleItemClick,
    handleHover,
    handleUnhover,
    isHovering,
    onFocus,
    onBlur,
    confirmItemSelection,
    handleItemDoubleClick,
    isSelected,
    itemRef,
    preSelectItem,
    isHighlighted,
  };
}
